import { useState, useRef } from 'react';
import { MUSICAL_KEYS, type SongMode } from '@/types/song';

type SavePayload = {
  title: string;
  artist: string;
  key: string;
  mode: SongMode;
  bpm: number | null;
  content: string;
  is_public: boolean;
};

type Props = {
  song: (Partial<SavePayload> & {
    id?: string;
    usage_count?: number;
    created_by_name?: string;
    source_song_id?: string | null;
  }) | null;
  onSave: (payload: SavePayload) => void;
  onCancel: () => void;
  onBack?: () => void;
  readOnly?: boolean;
  canChangePublic?: boolean;
};

const SongEditor = ({ song, onSave, onCancel, onBack, readOnly = false, canChangePublic = false }: Props) => {
  const [form, setForm] = useState<SavePayload>({
    title: song?.title ?? '',
    artist: song?.artist ?? '',
    key: song?.key ?? 'G',
    mode: song?.mode ?? 'MAJOR',
    bpm: song?.bpm ?? 72,
    content: song?.content ?? '',
    is_public: song?.is_public ?? false,
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // TX33: detect unmatched braces (non-blocking warning)
  const hasUnmatchedBrace = (text: string) => {
    let depth = 0;
    for (const ch of text) {
      if (ch === '{') depth++;
      else if (ch === '}') depth--;
      if (depth < 0) return true;
    }
    return depth !== 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(form);
  };

  // TX33: insert `{}` at cursor and place cursor between braces
  const insertAnnotationTag = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = form.content.slice(0, start);
    const selected = form.content.slice(start, end);
    const after = form.content.slice(end);
    const inserted = `{${selected}}`;
    const newContent = before + inserted + after;
    setForm({ ...form, content: newContent });
    // Restore focus and position cursor inside the braces
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + 1 + selected.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/5 bg-[#0D0C09]">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="md:hidden w-8 h-8 flex items-center justify-center text-white/50 hover:text-white cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
          )}
          <h2 className="text-white font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {readOnly ? 'Visualizar música' : song?.id ? 'Editar música' : 'Nova música'}
          </h2>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-white/10 text-white/50 hover:text-white rounded-lg text-sm cursor-pointer transition-colors whitespace-nowrap"
          >
            {readOnly ? 'Fechar' : 'Cancelar'}
          </button>
          {!readOnly && (
            <button
              type="submit"
              form="song-editor-form"
              className="px-4 py-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-lg text-sm cursor-pointer hover:bg-[#D4B87A] transition-colors whitespace-nowrap"
            >
              Salvar
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-8">
        <form id="song-editor-form" onSubmit={handleSubmit} className="max-w-3xl mx-auto flex flex-col gap-6">
          {song?.usage_count !== undefined && (
            <p className="text-white/40 text-sm">Usada por {song.usage_count} pessoas</p>
          )}
          {canChangePublic && (
            <label className="flex items-center gap-3 text-white/70 text-sm">
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                className="accent-[#C8A96E]"
              />
              Disponibilizar como música pública
            </label>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Nome da música"
                required
                disabled={readOnly}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">Artista</label>
              <input
                type="text"
                value={form.artist}
                onChange={(e) => setForm({ ...form, artist: e.target.value })}
                placeholder="Nome do artista"
                disabled={readOnly}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">Tom original</label>
              <select
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                disabled={readOnly}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
              >
                <option value="" className="bg-[#1A1915]">— sem tom —</option>
                {MUSICAL_KEYS.map((k) => (
                  <option key={k} value={k} className="bg-[#1A1915]">{k}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">Tonalidade</label>
              <div role="group" aria-label="Tonalidade" className="flex h-[46px] items-center rounded-xl border border-white/10 bg-white/5 p-1">
                {(['MAJOR', 'MINOR'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={form.mode === mode}
                    onClick={() => setForm({ ...form, mode })}
                    disabled={readOnly}
                    className={`h-full flex-1 rounded-lg px-3 text-sm transition-colors ${
                      form.mode === mode
                        ? 'bg-[#C8A96E]/20 text-[#C8A96E]'
                        : 'text-white/45 hover:text-white/75'
                    }`}
                  >
                    {mode === 'MAJOR' ? 'Maior' : 'Menor'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">BPM</label>
              <input
                type="number"
                value={form.bpm ?? ''}
                onChange={(e) => setForm({ ...form, bpm: e.target.value ? Number(e.target.value) : null })}
                min={40}
                max={300}
                placeholder="ex: 72"
                disabled={readOnly}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="text-white/40 text-xs uppercase tracking-widest mb-2 block">Cifra</label>
            <p className="text-white/20 text-xs mb-3">Cole a cifra completa. Use [Verso], [Refrão], [Ponte] para organizar.</p>
            {/* TX33: annotation toolbar */}
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={insertAnnotationTag}
                title="Inserir anotação {}"
                disabled={readOnly}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors border"
                style={{ backgroundColor: '#d9770615', borderColor: '#d97706', color: '#d97706' }}
              >
                <i className="ri-price-tag-3-line text-sm" />
                Anotação <kbd className="opacity-50 font-mono">{`{}`}</kbd>
              </button>
              {hasUnmatchedBrace(form.content) && (
                <span className="text-xs flex items-center gap-1" style={{ color: '#f87171' }}>
                  <i className="ri-error-warning-line" /> Chave sem par detectada
                </span>
              )}
            </div>
            <textarea
              ref={textareaRef}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              disabled={readOnly}
              placeholder={`[Intro]\nG    D    Em    C\n\n[Verso]\nG                D\nLetra da música aqui...`}
              rows={20}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors font-mono resize-none"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default SongEditor;
